import { isEmpty } from 'lodash-es';
import { ICandidate } from 'wisestep-api';
import create from 'zustand';

import {
  ITrackCvViewJobBoards,
  ITrackCvPages,
} from '../features/conversationWidget/utils/cvHelper';

interface ILatestResume {
  name: string;
  url: string;
}

export interface IConversationWidgetStore {
  id: string | null;
  tempConv: ICandidate | null;
  pipelineCandidateId: string | null;
  footerAction: string | null;
  taskFilterJobId: string | null;
  changeJob: boolean;
  candidate: ICandidate | null;
  navigate: 'prev' | 'next' | null;
  navigationState: boolean;
  fromPage: string | null;
  view: 'mapping' | 'submission';
  action:
    | 'email'
    | 'sms'
    | 'call'
    | 'mapping'
    | 'submission'
    | 'task'
    | 'smslogs'
    | 'rtr'
    | 'question'
    | 'share'
    | null;
  selectedEmail: string | null;
  selectedPhoneNumber: string | null;
  searchQuery: string | null;
  activityConversation: ICandidate | null;
  activityConversationFor: 'mapping' | 'submission' | null;
  closeTab: boolean;
  isWidgetOpen: boolean;
  isMappingFormChanged: boolean;
  isAggLoaded: boolean;
  mapBeforeNavigation: boolean;
  eraseData: boolean;
  refreshSource: boolean;
  smsLogsContactNumber: string | null;
  isNativeSearch: boolean;
  getLatest: boolean;
  resumeUpdatedOn: string;
  updateJBViews: boolean;
  latestResume: ILatestResume | null;
  cvViewSourcePage: ITrackCvPages | ITrackCvViewJobBoards;
  setCvViewSourcePage: (arg: ITrackCvPages | ITrackCvViewJobBoards) => void;
  setIsNativeSearch(arg: boolean): void;
  setIsLatest(arg: boolean): void;
  updateId(args: string | null): void;
  setTempConv(args: ICandidate | null): void;
  setPipelineCandidateId(args: string | null): void;
  setFooterAction(args: string | null): void;
  setTaskFilterJobId(args: string | null): void;
  setChangeJob: (arg: boolean) => void;
  setCandidate(args: ICandidate | null): void;
  updateNavigationState(args: boolean): void;
  updateView(args: 'mapping' | 'submission'): void;
  updateAction(
    args:
      | 'email'
      | 'sms'
      | 'call'
      | 'mapping'
      | 'submission'
      | 'task'
      | 'rtr'
      | 'question'
      | 'share'
      | null
  ): void;
  updateSelectedEmail: (email: string | null) => void;
  updateSelectedPhoneNumber: (ph: string | null) => void;
  setSearchQuery: (arg: string | null) => void;
  updateFromPage: (ph: string | null) => void;
  updateActivityConversation: (arg: ICandidate | null) => void;
  setActivityConversationFor(args: 'mapping' | 'submission' | null): void;
  openModalWithAction: (
    arg1: string,
    arg2: string,
    arg3:
      | 'email'
      | 'sms'
      | 'call'
      | 'mapping'
      | 'submission'
      | 'task'
      | 'smslogs'
      | 'rtr'
      | 'question',
    args4?: 'mapping' | 'submission'
  ) => void;
  setCloseTab: (arg: boolean) => void;
  setIsWidgetOpen: (arg: boolean) => void;
  onWidgetClose: () => void;
  onNavigation: (args: 'prev' | 'next') => void;
  onIdUpdate: (args: string) => void;
  onCandidateUpdate: (args: ICandidate) => void;
  setIsMappingFormChanged: (args: boolean) => void;
  setContactDetails: (args: ICandidate) => void;
  setIsAggLoaded: (args: boolean) => void;
  setMapBeforeNavigation: (args: boolean) => void;
  setEraseData(args: boolean): void;
  setRefreshSource(args: boolean): void;
  setSmsLogsContactNumber(args: string | null): void;
  setResumeUpdatedOn(args: string): void;
  setUpdateJBViews(value: boolean): void;
  setLatestResume(value: ILatestResume | null): void;
}

export const useConversationWidgetStore = create<IConversationWidgetStore>((set, get) => ({
  id: null,
  tempConv: null,
  pipelineCandidateId: null,
  footerAction: null,
  taskFilterJobId: null,
  changeJob: false,
  candidate: null,
  navigate: null,
  navigationState: false,
  view: 'mapping',
  action: null,
  fromPage: null,
  selectedEmail: null,
  selectedPhoneNumber: null,
  activityConversation: null,
  activityConversationFor: null,
  closeTab: false,
  isWidgetOpen: false,
  searchQuery: null,
  isMappingFormChanged: false,
  isAggLoaded: false,
  mapBeforeNavigation: false,
  eraseData: false,
  refreshSource: false,
  smsLogsContactNumber: null,
  isNativeSearch: false,
  getLatest: false,
  resumeUpdatedOn: '',
  updateJBViews: false,
  latestResume: null,
  cvViewSourcePage: ITrackCvPages.WISESTEP,
  setCvViewSourcePage: (arg: ITrackCvPages | ITrackCvViewJobBoards) =>
    set({ cvViewSourcePage: arg }),
  updateId: (value: string | null) => {
    set({ taskFilterJobId: null });
    set({ latestResume: null });
    set({ footerAction: null });
    set({ smsLogsContactNumber: null });
    set({ id: value });
  },
  setTempConv: (value: ICandidate | null) => set({ tempConv: value }),
  setPipelineCandidateId: (value: string | null) => set({ pipelineCandidateId: value }),
  setCandidate: (value: ICandidate | null) => {
    set({ pipelineCandidateId: null });
    set({ taskFilterJobId: null });
    set({ footerAction: null });
    set({ latestResume: null });
    set({ smsLogsContactNumber: null });
    set({ candidate: value });
  },
  updateNavigationState: (value: boolean) => set({ navigationState: value }),
  setFooterAction: (value: string | null) => set({ footerAction: value }),
  setTaskFilterJobId: (value: string | null) => set({ taskFilterJobId: value }),
  setChangeJob: (value: boolean) => set({ changeJob: value }),
  updateView: (value: 'mapping' | 'submission') => set({ view: value }),
  updateAction: (
    value:
      | 'email'
      | 'sms'
      | 'call'
      | 'mapping'
      | 'submission'
      | 'task'
      | 'rtr'
      | 'question'
      | 'share'
      | null
  ) => set({ action: value }),
  updateSelectedEmail: (value: string | null) => set({ selectedEmail: value }),
  updateSelectedPhoneNumber: (value: string | null) => set({ selectedPhoneNumber: value }),
  updateFromPage: (value: string | null) => set({ fromPage: value }),
  updateActivityConversation: (value: ICandidate | null) => {
    if (value == null) {
      set({ activityConversationFor: null });
    }
    set({ activityConversation: value });
  },
  setActivityConversationFor: (value: 'mapping' | 'submission' | null) =>
    set({ activityConversationFor: value }),
  openModalWithAction: (id, fromPage, action, view) => {
    set({ fromPage: fromPage });
    set({ footerAction: null });
    set({ action: action });
    set({ isWidgetOpen: true });
    set({ taskFilterJobId: null });
    set({ id: id });
    view && set({ view: view });
  },
  setCloseTab: (value: boolean) => set({ closeTab: value }),
  setIsWidgetOpen: (value: boolean) => set({ isWidgetOpen: value }),
  setSearchQuery: (value: string | null) => set({ searchQuery: value }),
  onWidgetClose: () => {
    set({ isWidgetOpen: false });
    set({ id: null });
    set({ pipelineCandidateId: null });
    set({ isMappingFormChanged: false });
    set({ candidate: null });
    set({ footerAction: null });
    set({ action: null });
    set({ activityConversation: null });
    set({ fromPage: null });
    set({ view: 'mapping' });
    set({ latestResume: null });
    set({ selectedEmail: null });
    set({ selectedPhoneNumber: null });
    set({ smsLogsContactNumber: null });
    set({ taskFilterJobId: null });
    if (get().closeTab) {
      set({ isNativeSearch: false, getLatest: false, resumeUpdatedOn: '' });
      window.close();
    }
  },
  onNavigation: (field: 'prev' | 'next') => {
    set({ id: null });
    set({ candidate: null });
    set({ pipelineCandidateId: null });
    set({ isMappingFormChanged: false });
    set({ action: null });
    set({ navigate: field });
    set({ footerAction: null });
    set({ navigationState: true });
    set({ activityConversation: null });
    set({ fromPage: null });
    set({ view: 'mapping' });
    set({ latestResume: null });
    set({ selectedEmail: null });
    set({ selectedPhoneNumber: null });
    set({ smsLogsContactNumber: null });
    set({ taskFilterJobId: null });
  },
  onIdUpdate: (id: string) => {
    set({ id: null });
    set({ isMappingFormChanged: false });
    set({ candidate: null });
    set({ footerAction: null });
    set({ action: null });
    set({ activityConversation: null });
    set({ smsLogsContactNumber: null });
    set({ taskFilterJobId: null });
    set({ latestResume: null });
    set({ view: 'mapping' });
    set({ id: id });
  },
  onCandidateUpdate: (candidate: ICandidate) => {
    set({ id: null });
    set({ isMappingFormChanged: false });
    set({ candidate: null });
    set({ footerAction: null });
    set({ action: null });
    set({ activityConversation: null });
    set({ smsLogsContactNumber: null });
    set({ taskFilterJobId: null });
    set({ latestResume: null });
    set({ view: 'mapping' });
    set({ candidate: candidate });
  },
  setIsMappingFormChanged: (args: boolean) => set({ isMappingFormChanged: args }),
  setContactDetails: (candidate: ICandidate) => {
    !isEmpty(candidate.email) && set({ selectedEmail: candidate.email });
    if (get().smsLogsContactNumber) {
      set({ selectedPhoneNumber: get().smsLogsContactNumber });
    } else if (!isEmpty(candidate.phoneNumber) || !isEmpty(candidate.alternatePhoneNumbers?.[0])) {
      set({ selectedPhoneNumber: candidate.phoneNumber ?? candidate.alternatePhoneNumbers?.[0] });
    }
  },
  setIsAggLoaded: (value: boolean) => set({ isAggLoaded: value }),
  setMapBeforeNavigation: (value: boolean) => set({ mapBeforeNavigation: value }),
  setEraseData: (value: boolean) => set({ eraseData: value }),
  setRefreshSource: (value: boolean) => set({ refreshSource: value }),
  setSmsLogsContactNumber: (value: string | null) => set({ smsLogsContactNumber: value }),
  setIsNativeSearch: (args: boolean) => set({ isNativeSearch: args }),
  setIsLatest: (args: boolean) => set({ getLatest: args }),
  setResumeUpdatedOn: (args: string) => set({ resumeUpdatedOn: args }),
  setUpdateJBViews: (args: boolean) => set({ updateJBViews: args }),
  setLatestResume: (args: ILatestResume) => set({ latestResume: args }),
}));
